<script setup lang="ts">
import { UiButton } from "@packages/component-library";
import OfferCard from "@/components/OfferCard.vue";
import { useLandingStore } from "@/modules/landing-module";
import { computed } from "vue";
import { useWindowSize } from "@vueuse/core";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { width } = useWindowSize();
const landingStore = useLandingStore();
const offers = computed(() => landingStore.offers);
</script>

<template>
  <section class="landing-get">
    <h3 class="container">{{ t("home.offers.title") }}</h3>
    <p class="container">{{ t("home.offers.subTitle") }}</p>
    <template v-if="width > 1239">
      <div class="landing-get__list">
        <div class="landing-get__slider">
          <OfferCard
            v-for="(offer, index) in offers.slice(0, 5)"
            :key="index"
            :offer="offer"
          />
        </div>
        <div class="landing-get__slider" aria-hidden="true">
          <OfferCard
            v-for="(offer, index) in offers.slice(0, 5)"
            :key="index"
            :offer="offer"
          />
        </div>
      </div>
      <div class="landing-get__list">
        <div class="landing-get__slider reversed">
          <OfferCard
            v-for="(offer, index) in offers.slice(5, 10)"
            :key="index"
            :offer="offer"
          />
        </div>
        <div class="landing-get__slider reversed" aria-hidden="true">
          <OfferCard
            v-for="(offer, index) in offers.slice(5, 10)"
            :key="index"
            :offer="offer"
          />
        </div>
      </div>
    </template>
    <div v-else class="landing-get__list">
      <div class="landing-get__slider">
        <OfferCard
          v-for="(offer, index) in offers.slice(0, 6)"
          :key="index"
          :offer="offer"
        />
      </div>
      <div class="landing-get__slider" aria-hidden="true">
        <OfferCard
          v-for="(offer, index) in offers.slice(0, 6)"
          :key="index"
          :offer="offer"
        />
      </div>
    </div>
    <div class="container">
      <UiButton
        :label="t('home.offers.button')"
        color="primary"
        size="l"
        data-test-id="landing-button-offers"
        :to="{ name: 'OffersView' }"
      />
    </div>
  </section>
</template>

<style scoped lang="scss">
.landing-get {
  > h3 {
    text-align: center;
    @include h3();
    color: var(--color-semantic-content-normal-primary);
  }

  > p {
    text-align: center;
    margin: ($base-space * 4) auto 0;
    @include subtitle();
    color: var(--color-semantic-content-normal-secondary);
  }

  > .container {
    display: flex;
    align-items: center;
    justify-content: center;

    &:last-child {
      margin: calc($base-space * 16) auto 0;

      @media (max-width: 1239px) {
        margin: calc($base-space * 12) auto 0;
      }

      @media (max-width: 767px) {
        margin: calc($base-space * 8) auto 0;
      }
    }
  }

  &__list {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    gap: calc($base-space * 5);
    @include hideScroll();
    overflow: hidden;
    position: relative;
    width: 100vw;

    margin: calc($base-space * 6) 0 0;

    &:first-of-type {
      margin: calc($base-space * 16) 0 0;

      @media (max-width: 1239px) {
        margin: calc($base-space * 12) auto 0;
      }

      @media (max-width: 767px) {
        margin: calc($base-space * 8) auto 0;
      }
    }
  }

  &__slider {
    flex-shrink: 0;
    display: flex;
    align-items: flex-start;
    gap: calc($base-space * 6);
    counter-reset: item;
    justify-content: space-around;
    min-width: 100%;
    animation: scroll 60s linear infinite;

    @media (max-width: 767px) {
      gap: calc($base-space * 4);
    }

    > .offer-card {
      width: 292px;

      @media (max-width: 1239px) {
        width: 220px;
      }
    }
  }
}

.reversed {
  animation-direction: reverse;
}

@keyframes scroll {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(calc(-100% - 20px));
  }
}
</style>
